import { findSceneNode, type CurvaDocument } from "@/document/model/document";
import type { AnimationTarget } from "@/document/model/animation";
import type { VectorNetwork } from "@/document/model/vectorNetwork";

export type ExpandedVectorPoint =
  | { kind: "vertex"; nodeId: string; vertexId: string; translateConnectedHandles: boolean }
  | { kind: "handle"; nodeId: string; edgeId: string; endpoint: "from" | "to" };

export function expandVectorAnimationTargets(
  document: CurvaDocument,
  targets: AnimationTarget[],
): ExpandedVectorPoint[] {
  const expanded: ExpandedVectorPoint[] = [];
  const seenVertices = new Set<string>();

  for (const target of targets) {
    for (const point of expandVectorAnimationTarget(document, target)) {
      if (point.kind === "vertex") {
        const key = `${point.nodeId}:${point.vertexId}`;
        if (seenVertices.has(key)) {
          continue;
        }
        seenVertices.add(key);
      }
      expanded.push(point);
    }
  }

  return expanded;
}

export function expandVectorAnimationTarget(
  document: CurvaDocument,
  target: AnimationTarget,
): ExpandedVectorPoint[] {
  if (target.kind === "vectorVertex") {
    const node = findSceneNode(document, target.nodeId);
    if (node?.type !== "vectorNetwork") {
      return [];
    }
    if (!node.network.vertices.some(vertex => vertex.id === target.vertexId)) {
      return [];
    }
    return [{
      kind: "vertex",
      nodeId: target.nodeId,
      vertexId: target.vertexId,
      translateConnectedHandles: true,
    }];
  }

  if (target.kind === "vectorHandle") {
    const node = findSceneNode(document, target.nodeId);
    if (node?.type !== "vectorNetwork") {
      return [];
    }
    const edge = node.network.edges.find(candidate => candidate.id === target.edgeId);
    if (!edge?.[target.endpoint].handle) {
      return [];
    }
    return [{
      kind: "handle",
      nodeId: target.nodeId,
      edgeId: target.edgeId,
      endpoint: target.endpoint,
    }];
  }

  if (target.kind === "vectorEdge") {
    const node = findSceneNode(document, target.nodeId);
    if (node?.type !== "vectorNetwork") {
      return [];
    }
    const edge = node.network.edges.find(candidate => candidate.id === target.edgeId);
    if (!edge) {
      return [];
    }

    const vertexIds = [...new Set([edge.from.vertexId, edge.to.vertexId])];
    return vertexIds.flatMap(vertexId =>
      node.network.vertices.some(vertex => vertex.id === vertexId)
        ? [{
            kind: "vertex" as const,
            nodeId: target.nodeId,
            vertexId,
            translateConnectedHandles: true,
          }]
        : [],
    );
  }

  return [];
}

export function expandedVectorPointPosition(
  network: VectorNetwork,
  point: ExpandedVectorPoint,
): { x: number; y: number } | undefined {
  if (point.kind === "vertex") {
    const vertex = network.vertices.find(candidate => candidate.id === point.vertexId);
    return vertex ? { x: vertex.x, y: vertex.y } : undefined;
  }

  const edge = network.edges.find(candidate => candidate.id === point.edgeId);
  const handle = edge?.[point.endpoint].handle;
  return handle ? { x: handle.x, y: handle.y } : undefined;
}
